import React, { useState, useEffect } from 'react';
import { PDFDocument, StandardFonts, rgb } from 'pdf-lib';
import { ArrowRight, Check } from 'lucide-react';
import AdminLogin from './AdminLogin';
import AdminDashboard from './AdminDashboard';

const API_URL = '/api';
const RECAPTCHA_SITE_KEY = '6Lfgl2ssAAAAAKi_C-Rm1JkeLWcwXFnDS3vlaTl0';

function App() {
  const [currentPage, setCurrentPage] = useState('home');
  const [isAdmin, setIsAdmin] = useState(false);
  const [totalCount, setTotalCount] = useState(0);

  useEffect(() => {
    // Set page title
    document.title = 'Ex-Zionist List';

    const token = localStorage.getItem('adminToken');
    if (token) {
      setIsAdmin(true);
    }

    if (window.location.hash === '#/admin') {
      setCurrentPage('admin');
    }

    const script = document.createElement('script');
    script.src = `https://www.google.com/recaptcha/api.js?render=${RECAPTCHA_SITE_KEY}`;
    script.async = true;
    script.defer = true;
    document.head.appendChild(script);

    return () => {
      if (document.head.contains(script)) {
        document.head.removeChild(script);
      }
    };
  }, []);

  const handleAdminLogin = (token) => {
    localStorage.setItem('adminToken', token);
    setIsAdmin(true);
    setCurrentPage('admin-dashboard');
  };

  const handleAdminLogout = () => {
    localStorage.removeItem('adminToken');
    setIsAdmin(false);
    setCurrentPage('home');
  };

  if (currentPage === 'admin' && !isAdmin) {
    return <AdminLogin onLogin={handleAdminLogin} />;
  }

  if ((currentPage === 'admin' || currentPage === 'admin-dashboard') && isAdmin) {
    return <AdminDashboard onLogout={handleAdminLogout} />;
  }

  const handleCountUpdate = (newCount) => {
    setTotalCount(newCount);
  };

  const handleStorySubmitted = () => {
    setTotalCount(prev => prev + 1);
  };

  return (
    <div className="bg-black">
      {currentPage === 'home' ? (
        <HomePage onNavigate={() => setCurrentPage('form')} onAdminClick={() => setCurrentPage('admin')} totalCount={totalCount} onCountUpdate={handleCountUpdate} />
      ) : currentPage === 'form' ? (
        <FormPage onNavigate={() => setCurrentPage('home')} onStorySubmitted={handleStorySubmitted} />
      ) : null}
    </div>
  );
}

// Homepage Component
function HomePage({ onNavigate, onAdminClick, totalCount, onCountUpdate }) {
  const [stories, setStories] = useState([]);
  const [counter, setCounter] = useState(totalCount || 0);
  const [displayCounter, setDisplayCounter] = useState(0);
  const [visitorCount, setVisitorCount] = useState(0);
  const [displayVisitorCount, setDisplayVisitorCount] = useState(0);

  // Sync counter when parent increments it after a new submission
  useEffect(() => {
    if (totalCount > counter) {
      setCounter(totalCount);
    }
  }, [totalCount]);

  useEffect(() => {
    const fetchStories = async () => {
      try {
        const response = await fetch(`${API_URL}/stories`);
        const data = await response.json();
        setStories(data.filter(s => s.share));
      } catch (error) {
        console.error("Failed to fetch stories:", error);
      }
    };

    const fetchTotalCount = async () => {
      try {
        const response = await fetch(`${API_URL}/total-submissions`);
        const data = await response.json();
        setCounter(data.total);
        onCountUpdate(data.total);
      } catch (error) {
        console.error("Failed to fetch total submissions:", error);
      }
    };

    const fetchVisitorCount = async () => {
      try {
        const response = await fetch(`${API_URL}/total-visitors`);
        const data = await response.json();
        setVisitorCount(data.total);
      } catch (error) {
        console.error("Failed to fetch visitor count:", error);
      }
    };

    fetchStories();
    fetchTotalCount();
    fetchVisitorCount();
  }, [onCountUpdate]);

  useEffect(() => {
    let animationFrame;
    let currentValue = 0;
    const targetValue = counter;
    // Adaptive duration: scale based on number size, max 2 seconds
    const duration = Math.min(2000, Math.max(500, counter / 1000));
    const startTime = Date.now();

    const animate = () => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / duration, 1);
      currentValue = Math.floor(targetValue * progress);
      setDisplayCounter(currentValue);
      if (progress < 1) {
        animationFrame = requestAnimationFrame(animate);
      }
    };
    animate();
    return () => cancelAnimationFrame(animationFrame);
  }, [counter]);

  // Animate visitor count
  useEffect(() => {
    let animationFrame;
    let currentValue = 0;
    const targetValue = visitorCount;
    const duration = Math.min(2000, Math.max(500, visitorCount / 1000));
    const startTime = Date.now();

    const animate = () => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / duration, 1);
      currentValue = Math.floor(targetValue * progress);
      setDisplayVisitorCount(currentValue);
      if (progress < 1) {
        animationFrame = requestAnimationFrame(animate);
      }
    };
    animate();
    return () => cancelAnimationFrame(animationFrame);
  }, [visitorCount]);

  return (
    <div className="relative bg-black text-white min-h-screen overflow-hidden flex flex-col">
      <div className="absolute inset-0 z-0">
        <div className="absolute inset-0 bg-gradient-to-b from-black via-transparent to-black"></div>
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-red-900 rounded-full mix-blend-screen filter blur-3xl opacity-20 animate-blob"></div>
        <div className="absolute top-1/2 right-1/4 w-96 h-96 bg-green-900 rounded-full mix-blend-screen filter blur-3xl opacity-20 animate-blob animation-delay-2000"></div>
      </div>

      <div className="relative flex-1 max-w-5xl mx-auto px-4 py-16 z-10 w-full">
        <header className="text-center mb-20">
          <h1 className="text-6xl md:text-8xl font-extrabold text-white mb-4 tracking-tighter">
            EX-ZIONIST LIST
          </h1>
          <p className="text-xl md:text-2xl text-gray-300 max-w-3xl mx-auto">
            A global platform for individuals to share their journey of awakening.
          </p>
        </header>

        {stories.length > 0 && (
          <div className="mb-20">
            <div className="text-center mb-10">
              <h2 className="text-4xl font-bold text-white">Voices of Change</h2>
              <p className="text-gray-400">Real people sharing why they changed their perspective</p>
            </div>
            <div className="relative h-72 overflow-hidden border-t-2 border-b-2 border-red-500 py-4">
              <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-black z-10"></div>
              <div className="space-y-6 animate-scroll-vertical-fast">
                {[...stories, ...stories].map((story, idx) => (
                  <div key={idx} className="bg-gray-900 p-5 rounded-lg shadow-2xl border border-gray-800">
                    <p className="text-gray-200 text-lg italic">"{(story.now_know || story.nowKnow || '...')}"</p>
                    <p className="text-right text-md text-gray-500 mt-3">- {story.name || 'Anonymous'}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        <div className="text-center mb-20">
           <div className="inline-flex flex-col md:flex-row gap-4">
            <div className="inline-flex items-center gap-4 bg-gray-900 px-8 py-4 rounded-full border-2 border-green-500 shadow-2xl">
              <span className="text-3xl font-bold text-white">{displayCounter}</span>
              <span className="text-xl text-gray-300">Have Spoken</span>
            </div>
            <div className="inline-flex items-center gap-4 bg-gray-900 px-8 py-4 rounded-full border-2 border-blue-500 shadow-2xl">
              <span className="text-3xl font-bold text-white">{displayVisitorCount}</span>
              <span className="text-xl text-gray-300">Total Readers</span>
            </div>
          </div>
        </div>

        <div className="bg-gray-900 rounded-lg p-10 md:p-16 text-center border-2 border-gray-800 shadow-2xl">
          <h2 className="text-4xl font-bold text-white mb-6">Share Your Truth</h2>
          <p className="text-xl text-gray-300 mb-10 max-w-2xl mx-auto">
            Your story deserves to be heard. Stand with millions who have chosen awareness over ignorance. You can remain anonymous.
          </p>
          <button
            onClick={onNavigate}
            className="group px-10 py-5 bg-white text-black font-bold text-xl rounded-lg shadow-2xl hover:bg-gray-300 transition-all duration-300 transform hover:scale-105"
          >
            <span className="flex items-center gap-4 justify-center">
              <ArrowRight className="w-7 h-7" />
              Share Your Journey
            </span>
          </button>
        </div>

        <div className="text-center mt-12">
          <button
            onClick={onAdminClick}
            className="text-gray-400 hover:text-gray-200 text-sm underline transition-colors"
          >
            Admin
          </button>
        </div>
      </div>

      {/* Footer */}
      <footer className="relative z-10 bg-black border-t border-gray-800 py-6 text-center">
        <p className="text-gray-400 text-sm">
          BROUGHT TO YOU BY DEADLYDYMES ACTION ENTERTAINMENT - DECOLONIZE YOUR SCREEN<br />
          <a href="https://www.deadlydymes.com" target="_blank" rel="noopener noreferrer" className="text-gray-500 hover:text-gray-300">
            DEADLYDYMES.COM
          </a>
        </p>
      </footer>

      <style>{`
        @keyframes scroll-vertical-fast {
          0% { transform: translateY(0); }
          100% { transform: translateY(-50%); }
        }
        .animate-scroll-vertical-fast {
          animation: scroll-vertical-fast 18s linear infinite;
        }
        @keyframes blob {
          0% { transform: translate(0px, 0px) scale(1); }
          33% { transform: translate(30px, -50px) scale(1.1); }
          66% { transform: translate(-20px, 20px) scale(0.9); }
          100% { transform: translate(0px, 0px) scale(1); }
        }
        .animate-blob { animation: blob 7s infinite; }
        .animation-delay-2000 { animation-delay: 2s; }
      `}</style>
    </div>
  );
}

// Form Page Component
function FormPage({ onNavigate, onStorySubmitted }) {
  const [formData, setFormData] = useState({ email: '', name: '', usedToThink: '', nowKnow: '', share: false });
  const [errors, setErrors] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const validateEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const handleSubmit = async () => {
    const newErrors = {};
    if (!formData.email) newErrors.email = 'Email is required';
    else if (!validateEmail(formData.email)) newErrors.email = 'Please enter a valid email';
    if (!formData.usedToThink) newErrors.usedToThink = 'This field is required';
    if (!formData.nowKnow) newErrors.nowKnow = 'This field is required';

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setIsSubmitting(true);

    try {
      const recaptchaToken = await window.grecaptcha.execute(RECAPTCHA_SITE_KEY, { action: 'submit' });
      const response = await fetch(`${API_URL}/stories`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...formData, recaptchaToken }),
      });

      const data = await response.json();

      if (!response.ok) {
        setErrors({ email: data.error || 'An unknown error occurred.' });
        setIsSubmitting(false);
        return;
      }

      // Update counter immediately after successful submission
      onStorySubmitted();
      await generatePDF(formData);
      setSubmitted(true);
    } catch (error) {
      setErrors({ email: 'Failed to submit. Please try again.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const generatePDF = async (data) => {
    try {
      // Fetch the PDF template
      const templateUrl = '/certificate-frame-optimized.pdf';
      const existingPdfBytes = await fetch(templateUrl).then(res => res.arrayBuffer());
      
      // Load the PDF template
      const pdfDoc = await PDFDocument.load(existingPdfBytes);
      const pages = pdfDoc.getPages();
      const firstPage = pages[0];
      const { width, height } = firstPage.getSize();
      
      // Embed Times Roman fonts
      const font = await pdfDoc.embedFont(StandardFonts.TimesRoman);
      const boldFont = await pdfDoc.embedFont(StandardFonts.TimesRomanBold);
      
      // Position settings (in points, from bottom)
      const settings = {
        nameY: 380,
        nameFontSize: 28,
        usedToThinkY: 325,
        usedFontSize: 11,
        nowKnowY: 220,
        nowFontSize: 11,
        dateY: 66,
        dateFontSize: 10,
        textMaxWidth: 280
      };
      
      // Helper function to draw centered text
      const drawCenteredText = (page, text, y, fontSize, fontToUse, color = rgb(0.235, 0.235, 0.235)) => {
        const textWidth = fontToUse.widthOfTextAtSize(text, fontSize);
        const x = (width - textWidth) / 2;
        page.drawText(text, {
          x: x,
          y: y,
          size: fontSize,
          font: fontToUse,
          color: color
        });
      };
      
      // Helper function to draw wrapped text (centered)
      const drawWrappedText = (page, text, y, fontSize, fontToUse, maxWidth, lineHeight = 1.2) => {
        const words = text.split(' ');
        let lines = [];
        let currentLine = '';
        
        for (let word of words) {
          const testLine = currentLine ? currentLine + ' ' + word : word;
          const testWidth = fontToUse.widthOfTextAtSize(testLine, fontSize);
          
          if (testWidth > maxWidth && currentLine) {
            lines.push(currentLine);
            currentLine = word;
          } else {
            currentLine = testLine;
          }
        }
        if (currentLine) {
          lines.push(currentLine);
        }
        
        let currentY = y;
        for (let line of lines) {
          drawCenteredText(page, line, currentY, fontSize, fontToUse);
          currentY -= fontSize * lineHeight;
        }
      };
      
      // 1. Add NAME (if provided)
      if (data.name) {
        drawCenteredText(firstPage, data.name, settings.nameY, settings.nameFontSize, boldFont, rgb(0.157, 0.157, 0.157));
      }
      
      // 2. Add "I used to believe" TEXT
      drawWrappedText(firstPage, data.usedToThink, settings.usedToThinkY, settings.usedFontSize, font, settings.textMaxWidth);
      
      // 3. Add "Now I understand" TEXT
      drawWrappedText(firstPage, data.nowKnow, settings.nowKnowY, settings.nowFontSize, font, settings.textMaxWidth);
      
      // 4. Add DATE (centered)
      const dateStr = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
      drawCenteredText(firstPage, dateStr, settings.dateY, settings.dateFontSize, font, rgb(0.392, 0.392, 0.392));
      
      // Save and download the PDF
      const pdfBytes = await pdfDoc.save();
      const blob = new Blob([pdfBytes], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const filename = data.name 
        ? `${data.name.replace(/\s+/g, '-')}-my-journey-of-awakening.pdf`
        : 'my-journey-of-awakening.pdf';
      link.download = filename;
      link.click();
      URL.revokeObjectURL(url);
      
    } catch (error) {
      console.error('Error generating PDF:', error);
      throw error;
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (name === 'usedToThink' || name === 'nowKnow') {
      if (value.length > 250) return;
    }
    setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  if (submitted) {
    return (
      <div className="bg-black text-white min-h-screen flex items-center justify-center">
        <div className="text-center max-w-2xl mx-auto px-4">
          <div className="mb-8">
            <Check className="w-24 h-24 text-green-500 mx-auto" />
          </div>
          <h1 className="text-5xl font-bold mb-4">Thank You for Sharing!</h1>
          <p className="text-xl text-gray-300 mb-8">
            Your story has been submitted successfully and your PDF certificate has been downloaded.
          </p>
          <p className="text-lg text-gray-400 mb-12">
            Your voice matters. Together, we are creating a global movement of awakening and truth.
          </p>
          <button
            onClick={onNavigate}
            className="px-8 py-4 bg-white text-black font-bold text-lg rounded-lg hover:bg-gray-300 transition-all"
          >
            Return to Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-black text-white min-h-screen py-12 px-4">
      <div className="max-w-2xl mx-auto">
        <h1 className="text-5xl font-bold text-center mb-4">Share Your Journey</h1>
        <p className="text-center text-gray-300 mb-12">
          Tell us about your awakening. What did you used to believe, and what do you understand now?
        </p>

        <div className="space-y-6">
          <div>
            <label className="block text-sm font-semibold mb-2">Email *</label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="your@email.com"
              className="w-full px-4 py-3 bg-gray-900 border-2 border-gray-700 rounded-lg text-white focus:border-red-500 focus:outline-none"
            />
            {errors.email && <p className="text-red-500 text-sm mt-1">{errors.email}</p>}
          </div>

          <div>
            <label className="block text-sm font-semibold mb-2">Name (Optional)</label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              placeholder="Your name or pseudonym"
              className="w-full px-4 py-3 bg-gray-900 border-2 border-gray-700 rounded-lg text-white focus:border-red-500 focus:outline-none"
            />
          </div>

          <div>
            <label className="block text-sm font-semibold mb-2">I used to believe... *</label>
            <textarea
              name="usedToThink"
              value={formData.usedToThink}
              onChange={handleChange}
              placeholder="What were your previous beliefs? (max 250 characters)"
              maxLength="250"
              rows="4"
              className="w-full px-4 py-3 bg-gray-900 border-2 border-gray-700 rounded-lg text-white focus:border-red-500 focus:outline-none resize-none"
            />
            <p className="text-gray-400 text-sm mt-1">{formData.usedToThink.length}/250</p>
            {errors.usedToThink && <p className="text-red-500 text-sm mt-1">{errors.usedToThink}</p>}
          </div>

          <div>
            <label className="block text-sm font-semibold mb-2">Now I understand... *</label>
            <textarea
              name="nowKnow"
              value={formData.nowKnow}
              onChange={handleChange}
              placeholder="What do you understand now? (max 250 characters)"
              maxLength="250"
              rows="4"
              className="w-full px-4 py-3 bg-gray-900 border-2 border-gray-700 rounded-lg text-white focus:border-red-500 focus:outline-none resize-none"
            />
            <p className="text-gray-400 text-sm mt-1">{formData.nowKnow.length}/250</p>
            {errors.nowKnow && <p className="text-red-500 text-sm mt-1">{errors.nowKnow}</p>}
          </div>

          <div className="flex items-center gap-3">
            <input
              type="checkbox"
              name="share"
              checked={formData.share}
              onChange={handleChange}
              className="w-5 h-5 bg-gray-900 border-2 border-gray-700 rounded cursor-pointer accent-green-500"
            />
            <label className="text-sm text-gray-300">
              Share my story on the homepage ticker
            </label>
          </div>

          <button
            onClick={handleSubmit}
            disabled={isSubmitting}
            className="w-full px-6 py-4 bg-white text-black font-bold text-lg rounded-lg hover:bg-gray-300 transition-all disabled:opacity-50"
          >
            {isSubmitting ? 'Submitting...' : 'Submit & Download PDF'}
          </button>

          <button
            onClick={onNavigate}
            className="w-full px-6 py-3 bg-gray-900 text-white font-semibold rounded-lg hover:bg-gray-800 transition-all border border-gray-700"
          >
            Back to Home
          </button>
        </div>
      </div>

      <style>{`
        @keyframes scroll-vertical-fast {
          0% { transform: translateY(0); }
          100% { transform: translateY(-50%); }
        }
        .animate-scroll-vertical-fast {
          animation: scroll-vertical-fast 18s linear infinite;
        }
        @keyframes blob {
          0% { transform: translate(0px, 0px) scale(1); }
          33% { transform: translate(30px, -50px) scale(1.1); }
          66% { transform: translate(-20px, 20px) scale(0.9); }
          100% { transform: translate(0px, 0px) scale(1); }
        }
        .animate-blob { animation: blob 7s infinite; }
        .animation-delay-2000 { animation-delay: 2s; }
      `}</style>
    </div>
  );
}

export default App;